import React from 'react';
import styled from '@slicknode/stylemapper';
import { shortenHash } from '@soundxyz/utils/const';
import { UserHoverCardContent } from '@components/Feed/User/UserHoverCardContent';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import { UserHoverCard } from '@components/main-components/user/UserHoverCard';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment, GroupedUsersInfoFragmentDoc } from '@graphql/reactQuery';
import { SPACE } from '@utils/constants';
import { getSingularOrPlural } from '@utils/helpers';
import { webappUriPath } from '@utils/url';

const MAX_USERNAMES_TO_SHOW = 3;
const MAX_USERSNAMES_TO_SHOW_IF_MORE_THAN_MAX = 2;

export const GroupedUsernames = ({
  users,
  totalUsers,
  othersOnClick,
  variant,
}: {
  users: FragmentType<GroupedUsersInfoFragmentDoc>[];
  totalUsers: number;
  othersOnClick: ((e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void) | null;
  variant: 'notification' | 'social-proof';
}) => {
  const maxToShow =
    totalUsers <= MAX_USERNAMES_TO_SHOW ? totalUsers : MAX_USERSNAMES_TO_SHOW_IF_MORE_THAN_MAX;
  const usersData = React.useMemo(
    () => users.map(user => getFragment(GroupedUsersInfoFragmentDoc, user)),
    [users],
  );
  const topThreeUsers = React.useMemo(() => usersData.slice(0, maxToShow), [usersData, maxToShow]);
  const totalOtherUsers = totalUsers - maxToShow;

  const hasOthers = totalOtherUsers > 0;

  const othersText = (
    <OthersText variant={variant}>
      {totalOtherUsers}
      {SPACE}
      {getSingularOrPlural({
        word: 'other',
        count: totalOtherUsers,
      })}
      {SPACE}
    </OthersText>
  );

  const othersUI = othersOnClick ? (
    <button onClick={othersOnClick} className="z-above2 appearance-none">
      {othersText}
    </button>
  ) : (
    othersText
  );
  return (
    <>
      {topThreeUsers.map((user, index) => {
        const commaOrAnd = getCommaOrAnd(index, totalUsers);
        const isUserNameAddr = user.username === shortenHash(user.publicAddress, 5, 4);

        const username = (
          <div className="block">
            <AccessibleLink
              key={`${user.id}-${index}`}
              className="inline-flex max-w-[84px] items-center sm:max-w-[120px]"
              pathnameUri={webappUriPath(user.webappUri)}
            >
              <UsernameText variant={variant} className="block md:hidden">
                {isUserNameAddr ? shortenHash(user.publicAddress, 4, 3) : user.username}
              </UsernameText>
              <UsernameText variant={variant} className="hidden md:block">
                {user.username}
              </UsernameText>
            </AccessibleLink>
            {!commaOrAnd && <Text variant={variant}>{SPACE}</Text>}
          </div>
        );

        return (
          <React.Fragment key={`${user.id}-${index}`}>
            <div className="z-above1 hidden md:block">
              <UserHoverCard
                userHoverCardContent={<UserHoverCardContent userId={user.id} />}
                trigger={username}
                triggerAsChild
              />
            </div>

            <div className="z-above1 block md:hidden">{username}</div>

            {!!commaOrAnd && (
              <Text variant={variant}>
                {commaOrAnd === 'and' && SPACE}
                {commaOrAnd}
                {SPACE}
              </Text>
            )}
            {index === 1 && totalUsers >= MAX_USERNAMES_TO_SHOW && (
              <Text variant={variant}>and{SPACE}</Text>
            )}
          </React.Fragment>
        );
      })}
      {hasOthers && othersUI}
    </>
  );
};

export const getCommaOrAnd = (index: number, totalLength: number) => {
  if (totalLength === 1) return null;
  if (totalLength === 2) return index === 0 ? 'and' : null;
  if (totalLength >= 3) return index === 0 ? ',' : index === 1 ? ',' : null;
  return null;
};

const Text = styled('span', {
  variants: {
    variant: {
      notification: '',
      'social-proof': 'font-base text-base-s',
    },
  },
});

const OthersText = styled('span', 'inline-flex font-semibold font-base text-black', {
  variants: {
    variant: {
      notification: '',
      'social-proof': 'text-base-s',
    },
  },
});

const UsernameText = styled('span', 'overflow-hidden text-ellipsis whitespace-nowrap', {
  variants: {
    variant: {
      notification: 'font-semibold',
      'social-proof': 'font-base text-black font-semibold text-base-s',
    },
  },
});
